import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabaseClient';
import { sleep } from '../lib/utils';
import { SupabaseClient } from '@supabase/supabase-js';
import { authStateManager } from '../lib/authStateManager';
import { callerTeamAccess } from '../lib/teamAccess';
import { signOut } from '../lib/auth';
// DISABLED: MultiSessionManager - unused feature that was causing login/logout conflicts
// import { multiSessionManager } from '../services/multiSessionManager';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second
const NIL_UUID = '00000000-0000-0000-0000-000000000000';

/** JWT metadata sometimes stores [] or a nil UUID alongside the real id — both break .eq('id', …). */
function normalizeBusinessIds(raw: unknown): string[] {
  const asList = Array.isArray(raw) ? raw : raw != null && raw !== '' ? [raw] : [];
  const ids = asList
    .map((v) => String(v || '').trim())
    .filter((id) => id.length > 0 && id !== NIL_UUID);
  return [...new Set(ids)];
}

function firstNonEmptyBusinessIds(...candidates: unknown[]): string[] {
  for (const candidate of candidates) {
    const ids = normalizeBusinessIds(candidate);
    if (ids.length > 0) return ids;
  }
  return [];
}

interface BusinessHours {
  enabled: boolean;
  open: string;
  close: string;
  closed: boolean;
}

interface WeeklySchedule {
  monday: BusinessHours;
  tuesday: BusinessHours;
  wednesday: BusinessHours;
  thursday: BusinessHours;
  friday: BusinessHours;
  saturday: BusinessHours;
  sunday: BusinessHours;
}

interface BookingSettings {
  allow_online_bookings: boolean;
  require_deposit: boolean;
  send_confirmations: boolean;
  buffer_time: number;
  max_bookings_per_day: number | null;
  cancellation_policy: string;
  deposit_amount: number;
  deposit_type: 'percentage' | 'fixed';
  navbar_color?: string;
  logo_url?: string;
  logo_invert?: boolean;
}

interface NotificationSettings {
  email_notifications: boolean;
  sms_notifications: boolean;
  push_notifications: boolean;
  reminder_time: string;
  send_booking_confirmations: boolean;
  send_booking_reminders: boolean;
  send_booking_followups: boolean;
}

export interface Business {
  id: string;
  name: string;
  type: string;
  email: string;
  phone: string;
  business_domain_email: string;
  primary_email: string;
  email_verified: boolean;
  address: string;
  timezone: string;
  currency: string;
  business_hours: WeeklySchedule;
  booking_settings: BookingSettings;
  notification_settings: NotificationSettings;
  created_at: string;
  updated_at: string;
  owner_id: string;
  gst_number?: string;
  pst_number?: string;
  industry?: 'Automotive' | 'Home Services';
  slug: string;
  /** Verified custom hostname for public intake links (e.g. forms.acme.com). */
  intake_custom_hostname?: string | null;
  intake_custom_host_verified_at?: string | null;
  intake_custom_host_verification_token?: string | null;
  /** Verified custom hostname for public proposal links (e.g. proposals.acme.com). */
  proposal_custom_hostname?: string | null;
  proposal_custom_host_verified_at?: string | null;
  proposal_custom_host_verification_token?: string | null;
  /** Verified custom hostname for public feedback links (e.g. feedback.acme.com). */
  feedback_custom_hostname?: string | null;
  feedback_custom_host_verified_at?: string | null;
  feedback_custom_host_verification_token?: string | null;
  /** Live URL for Nexus-hosted customer site; set by super admin */
  nexus_website_url?: string | null;
  /** Vercel project linked from super admin. */
  vercel_project_id?: string | null;
  /** True when Vercel hosting is paused (payment, cancellation, or manual pause). */
  vercel_hosting_paused?: boolean | null;
  /** Stripe subscription id for website hosting (separate from platform subscription). */
  nexus_hosting_stripe_subscription_id?: string | null;
  nexus_hosting_subscription_status?: string | null;
  /** Synced from Stripe via webhooks */
  subscription_status?: string | null;
  /** First transition into past_due (Stripe sync); cleared when resolved */
  subscription_past_due_at?: string | null;
  active_plan_id?: string | null;
  frozen?: boolean | null;
  frozen_reason?: string | null;
  /** Agents area (Nexus general manager + specialists). Defaults on; off keeps the plain assistant. */
  agents_enabled?: boolean | null;
  /** Optional automation send-window config (quiet hours). Null/disabled = send anytime. */
  automation_delivery_hours?: AutomationDeliveryHours | null;
}

/** Per-day automation send window (business-local time). */
export interface AutomationDeliveryDay {
  enabled: boolean;
  start: string; // 'HH:mm'
  end: string;   // 'HH:mm'
}

export interface AutomationDeliveryHours {
  enabled: boolean;
  days: {
    monday: AutomationDeliveryDay;
    tuesday: AutomationDeliveryDay;
    wednesday: AutomationDeliveryDay;
    thursday: AutomationDeliveryDay;
    friday: AutomationDeliveryDay;
    saturday: AutomationDeliveryDay;
    sunday: AutomationDeliveryDay;
  };
}

interface BusinessContextType {
  selectedBusiness: Business | null;
  businessId: string | null;
  setSelectedBusiness: (business: Business | null) => void;
  businesses: Business[];
  role: string;
  loading: boolean;
  error: string | null;
  updateBusiness: (id: string, updates: Partial<Business>) => Promise<void>;
  /** Pass `{ silent: true }` for background refreshes (subscription sync / banner) so global loading stays off. */
  forceLoadBusinesses: (options?: { silent?: boolean }) => void;
  handleSuccessfulLogin: () => void;
}

export const BusinessContext = createContext<BusinessContextType | null>(null);

export function useBusiness() {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
}

interface BusinessProviderProps {
  children: ReactNode;
  business: Business | null;
}

export function BusinessProvider({ children, business }: BusinessProviderProps) {
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(business);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('admin');
  const [error, setError] = useState<string | null>(null);
  const [businessCount, setBusinessCount] = useState(0);
  const [businessId, setBusinessId] = useState<string | null>(business?.id || null);

  useEffect(() => {
    setBusinessId(selectedBusiness?.id || null);
  }, [selectedBusiness]);

  // CRITICAL: Reset ALL state when navigating to login page
  // This ensures BusinessContext is completely clean for new login
  // Use window.location.pathname instead of useLocation() to avoid Router context issues
  useEffect(() => {
    const checkPathname = () => {
      if (window.location.pathname === '/login') {
        console.log('🔄 BusinessContext: Detected navigation to /login - FULLY RESETTING all state');
        // Reset ALL state variables
        setBusinesses([]);
        setSelectedBusiness(null);
        setBusinessId(null);
        setRole('admin');
        setBusinessCount(0);
        setError(null);
        setLoading(false);
        
        // Force clear ALL business-related localStorage
        const keysToRemove = [
          'selectedBusiness',
          'businesses',
          'usePreloadedBusinessData',
          'homeScreenRoute',
          'skipInitializing',
          'preloadedRoute',
          'lastSelectedBusinessId',
          'businessContext',
          'userBusinesses',
          'currentBusiness',
          'enhancedLoginInProgress'
        ];
        keysToRemove.forEach(key => {
          try {
            localStorage.removeItem(key);
          } catch (e) {
            console.warn(`Failed to remove ${key}:`, e);
          }
        });
        
        console.log('✅ BusinessContext: Complete state reset on /login navigation');
      }
    };

    // Check immediately
    checkPathname();

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', checkPathname);
    
    // Also check periodically in case navigation happens via React Router
    const interval = setInterval(checkPathname, 100);

    return () => {
      window.removeEventListener('popstate', checkPathname);
      clearInterval(interval);
    };
  }, []);


  const loadBusinesses = async (
    retryCount = 0,
    mounted = true,
    loadOpts?: { silent?: boolean },
  ) => {
    // TEMPORARILY DISABLED: Skip loading during tab switches (check if page just became visible)
    // if (!retryCount && document.hidden) {
    //   console.log('⏭️ Skipping business load - page is hidden (tab switch)');
    //   return;
    // }

    if (!retryCount) {
      if (!loadOpts?.silent) {
        setLoading(true);
        setError(null);
      }
    }

    if (!supabase) {
      console.error('Supabase client not initialized');
      setError('Database connection not available');
      setLoading(false);
      return;
    }

    try {
      // First check if we have a valid session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      console.log('Auth session check:', { 
        hasSession: !!session, 
        userId: session?.user?.id,
        userMetadata: session?.user?.user_metadata,
        error: sessionError
      });
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        throw sessionError;
      }

      if (session?.user) {
        const teamAccess = await callerTeamAccess();
        if (!mounted) return;
        if (teamAccess === 'deactivated') {
          setBusinesses([]);
          setSelectedBusiness(null);
          setBusinessId(null);
          setRole('admin');
          setBusinessCount(0);
          setError(null);
          setLoading(false);
          await signOut();
          return;
        }
      }

      if (!session?.user) {
        console.log('No user session found, clearing ALL business state');
        // CRITICAL: Clear ALL business state, not just selected business
        setBusinesses([]);
        setSelectedBusiness(null);
        setBusinessId(null);
        setRole('admin');
        setBusinessCount(0);
        setError(null);
        setLoading(false);
        return;
      }

      // Prefer employed → owned → single business_id. Skip empty arrays and nil UUIDs
      // (those used to make .eq('id', [...]) return no rows → infinite login spinner).
      let businessIds = firstNonEmptyBusinessIds(
        session.user.user_metadata.employed_business_ids,
        session.user.user_metadata.owned_business_ids,
        session.user.user_metadata.business_id,
      );

      console.log('Business ID check:', {
        employedBusinessIds: session.user.user_metadata.employed_business_ids,
        ownedBusinessIds: session.user.user_metadata.owned_business_ids,
        businessId: session.user.user_metadata.business_id,
        finalBusinessIds: businessIds,
      });

      // If no business ID found in metadata, check if user is a resource
      if (businessIds.length === 0) {
        console.log('No business ID in metadata, checking if user is a resource...');
        
        try {
          const { data: resourceRows, error: resourceError } = await supabase
            .from('resources')
            .select('business_id')
            .eq('auth_user_id', session.user.id)
            .eq('is_active', true)
            .order('created_at', { ascending: false })
            .limit(1);
          const resourceData = resourceRows?.[0];

          if (!resourceError && resourceData?.business_id) {
            businessIds = normalizeBusinessIds(resourceData.business_id);
            console.log('Found business ID from resource table:', businessIds);
          } else {
            console.error('Error getting resource business ID:', resourceError);
            
            // Try alternative query without is_active filter
            const { data: altResourceData, error: altResourceError } = await supabase
              .from('resources')
              .select('business_id')
              .eq('auth_user_id', session.user.id)
              .single();

            if (!altResourceError && altResourceData?.business_id) {
              businessIds = normalizeBusinessIds(altResourceData.business_id);
              console.log('Found business ID from resource table (without active filter):', businessIds);
            } else {
              console.error('Alternative resource query also failed:', altResourceError);
              
              // Final fallback: try to find resource by email
              console.log('Trying email-based resource lookup...');
              const { data: emailResourceData, error: emailResourceError } = await supabase
                .from('resources')
                .select('business_id')
                .eq('email', session.user.email)
                .single();

              if (!emailResourceError && emailResourceData?.business_id) {
                businessIds = normalizeBusinessIds(emailResourceData.business_id);
                console.log('Found business ID from resource table (by email):', businessIds);
              } else {
                console.error('Email-based resource query also failed:', emailResourceError);
                throw new Error('Unable to determine business context');
              }
            }
          }
        } catch (error) {
          console.error('Failed to get resource business ID:', error);
          throw new Error('Unable to determine business context');
        }
      }

      if (businessIds.length === 0) {
        throw new Error('Unable to determine business context');
      }

      const ownedIds = normalizeBusinessIds(session.user.user_metadata.owned_business_ids);
      if (ownedIds.length > 0) {
        console.log('User is admin');
        setRole('admin');
      } else {
        console.log('Checking resource role');
        const { data: resourceRole, error: roleError } = await supabase
          .from('resources')
          .select('role, auth_user_id')
          .eq('auth_user_id', session.user.id)
          .single();

        console.log('Resource role check:', { resourceRole, error: roleError });

        if (!roleError && resourceRole) {
          setRole(resourceRole.role);
        } else {
          console.error('Error getting resource role:', roleError);
        }
      }

      // Get business data with retry — use .in() so multi-id metadata still works
      let businessData;
      let businessError;
      for (let i = 0; i < 3; i++) {
        console.log(`Attempting to load business data (attempt ${i + 1}/3)`, businessIds);
        const result = await supabase
          .from('businesses')
          .select('*')
          .in('id', businessIds)
          .order('created_at', { ascending: false });

        if (!result.error) {
          businessData = result.data;
          console.log('Business data loaded:', businessData);
          break;
        }
        businessError = result.error;
        console.error(`Business load attempt ${i + 1} failed:`, businessError);
        await sleep(RETRY_DELAY * Math.pow(2, i));
      }

      if (businessError) throw businessError;

      const nextBusinesses = businessData || [];
      if (nextBusinesses.length === 0) {
        throw new Error('Unable to load your business. Please contact support if this continues.');
      }
      const isSilent = !!loadOpts?.silent;

      // The background poll (silent) exists ONLY to surface subscription-status
      // changes for the past-due banner. The business row has fields that flap on
      // every fetch, so a naive full-object compare always produces a new reference
      // — which re-renders BusinessContext, which (via a nested conversation view)
      // remounts the messages composer every 60s and wipes the user's draft.
      //
      // So on SILENT refreshes we only adopt the new object when a UI-relevant
      // field actually changed; otherwise we preserve the existing reference so the
      // provider doesn't re-render at all. Non-silent refreshes (login / account
      // switch / edits) still do a full update so fresh data is never stale.
      const uiRelevantChanged = (a: Business, b: Business) =>
        a.subscription_status !== b.subscription_status ||
        a.subscription_past_due_at !== b.subscription_past_due_at ||
        a.nexus_hosting_subscription_status !== b.nexus_hosting_subscription_status ||
        a.nexus_website_url !== b.nexus_website_url ||
        a.vercel_project_id !== b.vercel_project_id ||
        a.vercel_hosting_paused !== b.vercel_hosting_paused;

      setBusinesses((prev) => {
        if (isSilent) {
          const sameSet =
            prev.length === nextBusinesses.length &&
            prev.every((p, i) => p.id === nextBusinesses[i]?.id);
          const anyUiChanged = nextBusinesses.some((nb) => {
            const old = prev.find((p) => p.id === nb.id);
            return !old || uiRelevantChanged(old, nb);
          });
          return sameSet && !anyUiChanged ? prev : nextBusinesses;
        }
        const unchanged =
          prev.length === nextBusinesses.length &&
          JSON.stringify(prev) === JSON.stringify(nextBusinesses);
        return unchanged ? prev : nextBusinesses;
      });
      setBusinessCount(nextBusinesses.length);

      if (nextBusinesses.length > 0) {
        // Keep the currently-selected business (and its object reference) when it
        // still exists and hasn't meaningfully changed; only swap when it truly
        // changed or went away. This is the key fix for the periodic state-reset bug.
        setSelectedBusiness((prev) => {
          if (prev) {
            const match = nextBusinesses.find((b) => b.id === prev.id);
            if (match) {
              if (isSilent) {
                return uiRelevantChanged(prev, match) ? match : prev;
              }
              return JSON.stringify(match) === JSON.stringify(prev) ? prev : match;
            }
          }
          return nextBusinesses[0];
        });
      } else {
        // No businesses found - clear selected business
        setSelectedBusiness(null);
      }

      // Keep businessId in sync, preserving the current id when it still exists.
      setBusinessId((prevId) => {
        if (nextBusinesses.length === 0) return null;
        if (prevId && nextBusinesses.some((b) => b.id === prevId)) return prevId;
        return nextBusinesses[0].id;
      });

      // DISABLED: MultiSessionManager was part of unused multi-account feature
      // It was causing login/logout conflicts by managing multiple sessions
      // Removed to keep login/logout simple and reliable

      setError(null);
    } catch (err) {
      console.error('Error in loadBusinesses:', err);

      // Retry on network errors
      if (
        retryCount < MAX_RETRIES && 
        (err instanceof Error && err.message.includes('Failed to fetch'))
      ) {
        console.log(`Retrying business load (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
        await sleep(RETRY_DELAY * Math.pow(2, retryCount));
        return loadBusinesses(retryCount + 1, mounted, loadOpts);
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to load businesses';
      setError(errorMessage);
      setBusinesses([]);
      setSelectedBusiness(null);
      console.log('❌ BusinessContext: Business loading failed:', errorMessage);
    } finally {
      if (mounted) {
        setLoading(false);
        console.log('✅ BusinessContext: Loading state cleared');
      }
    }
  };

  useEffect(() => {
    let mounted = true;
    const client = supabase;

    if (!client) {
      console.error('Supabase client not initialized');
      return;
    }

    // Initialize authStateManager to ensure it's working
    authStateManager.initialize();

    // CRITICAL: Listen for SIGNED_OUT events to fully reset state
    // This ensures BusinessContext is completely clean for new login
    const authUnsubscribe = authStateManager.subscribe((event) => {
      if (event === 'SIGNED_OUT') {
        console.log('🔄 BusinessContext: SIGNED_OUT detected - FULLY RESETTING all state');
        if (mounted) {
          // Reset ALL state variables to ensure fresh start
          setBusinesses([]);
          setSelectedBusiness(null);
          setLoading(false);
          setError(null);
          setRole('admin');
          setBusinessCount(0);
          setBusinessId(null);
          
          // Force clear ALL business-related localStorage
          // CRITICAL: Also clear MultiSessionManager data (unused feature causing conflicts)
          const keysToRemove = [
            'selectedBusiness',
            'businesses',
            'usePreloadedBusinessData',
            'homeScreenRoute',
            'skipInitializing',
            'preloadedRoute',
            'lastSelectedBusinessId',
            'businessContext',
            'userBusinesses',
            'currentBusiness',
            'activeBusinessSessions', // CRITICAL: Clear MultiSessionManager sessions
            'currentActiveSessionId', // CRITICAL: Clear MultiSessionManager current session
          ];
          keysToRemove.forEach(key => {
            try {
              localStorage.removeItem(key);
            } catch (e) {
              console.warn(`Failed to remove ${key}:`, e);
            }
          });
          
          console.log('✅ BusinessContext: COMPLETE state reset - ready for fresh login');
      }
      }
    });

    // Timeout to prevent infinite loading - reduced for faster startup
    const loadingTimeout = setTimeout(() => {
      console.warn('⚠️ BusinessContext: Loading timeout reached, clearing loading state');
      if (mounted) {
        setLoading(false);
        setError('Connection timeout. Please refresh the page to try again.');
      }
    }, 3000); // 3 second timeout (reduced from 5) for faster startup

    // SIMPLE: NEVER use localStorage for business data - always load fresh from database
    // This eliminates all race conditions, stale data, and verification complexity
    // When user logs in, BusinessContext detects session and loads businesses from database
    
    // SIMPLE: Always check session and load from database - never trust existing state
    const checkSessionWithRetry = async (attempt = 1) => {
      if (!mounted) return; // Stop if unmounted
      
      // CRITICAL: ALWAYS clear state FIRST - never trust existing businesses state
      // This ensures we never use stale data from previous login
      setBusinesses([]);
      setSelectedBusiness(null);
      setBusinessId(null);
      
      try {
        const { session, error } = await authStateManager.getSession();
        if (!error && session?.user) {
          // Session exists - load businesses fresh from database
          setLoading(true);
          setError(null);
          await loadBusinesses(0, mounted);
          return; // Success - stop retrying
        } else {
          // No session - clear everything
          setBusinesses([]);
          setSelectedBusiness(null);
          setBusinessId(null);
          setLoading(false);
        }
      } catch (error) {
        // Silent retry
      }
      
      // Retry up to 3 times with 200ms delay (reduced from 5 attempts with 500ms)
      // Faster retries = faster startup
      if (attempt < 3) {
        setTimeout(() => checkSessionWithRetry(attempt + 1), 200);
      } else {
        if (mounted) {
          setLoading(false);
          setError('Unable to establish business context. Please refresh the page.');
        }
      }
    };
    
    const unsubscribe = () => {
      // No cleanup needed for this approach
    };

    // Always check for session and load businesses if needed
    // SIMPLE: Just check session and load from database - no localStorage complexity
      checkSessionWithRetry();
    
    // Also add a fallback check after a short delay to catch cases where
    // the auth state hasn't been established yet (reduced delay for faster startup)
    setTimeout(() => {
      if (mounted && !selectedBusiness && businesses.length === 0 && !loading) {
        console.log('🔄 BusinessContext: Fallback session check - no business data loaded yet');
        checkSessionWithRetry();
      }
    }, 1000); // Reduced from 2000ms to 1000ms for faster startup

    return () => {
      mounted = false;
      unsubscribe();
      authUnsubscribe(); // Clean up auth listener
      clearTimeout(loadingTimeout);
    };
  }, []);

  const updateBusiness = async (id: string, updates: Partial<Business>) => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    try {
      const { data, error } = await supabase
        .from('businesses')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setBusinesses((prev) => prev.map((b) => (b.id === id ? data : b)));
      if (selectedBusiness?.id === id) {
        setSelectedBusiness(data);
      }
    } catch (err) {
      console.error('Error updating business:', err);
      throw err;
    }
  };


  const value = {
    selectedBusiness,
    businessId,
    setSelectedBusiness,
    businesses,
    role,
    loading,
    error,
    updateBusiness,
    // Add manual loading function for debugging
    forceLoadBusinesses: (options?: { silent?: boolean }) => {
      loadBusinesses(0, true, options);
    },
    // NEW: Method for login flow to trigger business loading
    handleSuccessfulLogin: () => {
      // SIMPLE: Always reload from database on login - never trust existing state
      // This ensures we always have fresh data for the logged-in user
      console.log('🔄 handleSuccessfulLogin: Loading businesses fresh from database');
      setBusinesses([]);
      setSelectedBusiness(null);
      setBusinessId(null);
        setLoading(true);
        setError(null);
        loadBusinesses(0, true);
    }
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
}

// Export as a single default object
const BusinessModule = {
  Provider: BusinessProvider,
  useBusiness
};

export default BusinessModule;
